import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useHistory, useLocation } from "react-router-dom";
import { Modal, ModalBody, ModalHeader } from "reactstrap";
import notifications from "../../common/Components/Notifications/Notification";
import { canManageKarkhana } from "../../helpers/karkhanaEnabled";
import { createCosting, listCostings, listKarkhanaDesigns } from "../../services/karkhanaApi";
import "../gi-registry/gi-registry.scss";
import PageHeader, { GearIcon, HeaderButton } from "./ui/PageHeader";
import SearchField from "./ui/SearchField";
import { useKT } from "./i18n/kt";
import { fmtDay, rupees } from "./rates/rateModel";
import { STATUS_LABEL, localDay } from "./costing/costingModel";
import "./costing/karkhana-costing.scss";
import { kp } from "./karkhanaPaths";
import { useAddressParam } from "./ui/addressState";

const readRole = () => {
  try {
    return JSON.parse(localStorage.getItem("authUser") || "null")?.users?.role;
  } catch {
    return undefined;
  }
};

const FILTERS: { id: string; title: string }[] = [
  { id: "", title: "All" },
  { id: "DRAFT", title: "Drafts" },
  { id: "SUBMITTED", title: "Waiting for approval" },
  { id: "REJECTED", title: "Sent back" },
  { id: "APPROVED", title: "Approved" },
];

/**
 * Every saree's costing: drafts, ones waiting for the owner, approved ones with their prices, and the
 * finished sarees that still have none. Owner/admins and managers only.
 */
const KarkhanaCostingsPage = () => {
  const { t } = useKT();
  const history = useHistory();
  const location = useLocation();
  const role = readRole();
  // Status and search are in the address (replaced, not pushed): Back from a costing keeps them.
  const [status, setStatus] = useAddressParam("status");
  const [q, setQ] = useAddressParam("q");
  const [data, setData] = useState<any>(null);
  const [busy, setBusy] = useState(false);
  const [picking, setPicking] = useState(false);
  const [designQ, setDesignQ] = useState("");
  const [designs, setDesigns] = useState<any[]>([]);
  const opened = useRef(false);

  const load = useCallback(
    async (query = q, st = status) => {
      try {
        const res: any = await listCostings({ status: st || undefined, q: query.trim() || undefined });
        if (res?.success) setData(res.data);
        else notifications({ title: t("Error"), message: t(res?.message || "Could not load"), type: "danger" });
      } catch {
        notifications({ title: t("No connection"), message: t("This needs a connection. Try again in a moment."), type: "warning" });
      }
    },
    [q, status] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const start = useCallback(
    async (body: { pieceId?: string; sareeNumber?: string; designId?: string }, replace = false) => {
      setBusy(true);
      try {
        const res: any = await createCosting(body);
        if (!res?.success) {
          notifications({ title: t("Not done"), message: t(res?.message || "Could not start the costing"), type: "danger" });
          return;
        }
        // From an email link (?saree=): replace, so Back does not land here and start it again.
        if (replace) history.replace(kp.costing(res.data.costing.id));
        else history.push(kp.costing(res.data.costing.id));
      } catch {
        notifications({ title: t("No connection"), message: t("Try again in a moment."), type: "warning" });
      } finally {
        setBusy(false);
      }
    },
    [history] // eslint-disable-line react-hooks/exhaustive-deps
  );

  useEffect(() => {
    if (!canManageKarkhana(role)) {
      history.replace(kp.home());
      return;
    }
    // From the "costing needed" email: open (or start) that saree's costing.
    const saree = new URLSearchParams(location.search).get("saree");
    if (saree && !opened.current) {
      opened.current = true;
      start({ sareeNumber: saree }, true);
      return;
    }
    load(q, status);
  }, [role]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!picking) return;
    const timer = window.setTimeout(() => {
      listKarkhanaDesigns({ q: designQ.trim() || undefined, limit: 30 })
        .then((res: any) => setDesigns(Array.isArray(res?.data?.docs) ? res.data.docs : []))
        .catch(() => setDesigns([]));
    }, 200);
    return () => window.clearTimeout(timer);
  }, [picking, designQ]);

  if (!canManageKarkhana(role)) return null;

  const rows: any[] = data?.costings || [];
  const waiting: any[] = data?.sareesWithoutCosting || [];

  return (
    <div className="gi-registry-page kw-page kc-page kc-list">
      <PageHeader title={t("Costing")} back={kp.more()}>
        <HeaderButton onClick={() => setPicking(true)} add>
          {t("Estimate for a design")}
        </HeaderButton>
        <Link to={kp.costingLines()} className="kh-iconbtn" title={t("Costing lines and material rates")} aria-label={t("Costing lines and material rates")}>
          <GearIcon />
        </Link>
      </PageHeader>

      <p className="kw-lede">
        {t("Every saree gets a costing: material, weaving, preparation, setup and finishing, then overhead and profit. Every line needs a number — 0 is fine, empty is not. The owner approves; approval sets the product's cost and sale price.")}
      </p>

      {waiting.length ? (
        <>
          <h3 className="kc-group__title">
            <span>{t("Sarees without a costing")}</span>
            <small>{waiting.length}</small>
          </h3>
          <div className="kc-waiting">
            {waiting.slice(0, 24).map((w) => (
              <button key={w.pieceId} type="button" onClick={() => start({ pieceId: w.pieceId })} disabled={busy}>
                <strong>
                  {t("Saree")} {w.sareeNumber}
                </strong>
                <small>
                  {w.designNumber} · {w.loomCode} · {w.status === "COMPLETED" ? t("finished {{d}}", { d: fmtDay(w.finishedOn) }) : t("on the loom")}
                </small>
              </button>
            ))}
          </div>
        </>
      ) : null}

      <SearchField
        value={q}
        onChange={setQ}
        onSubmit={(v) => load(v, status)}
        placeholder={t("Saree number, design or product")}
        label={t("Search costings")}
        count={data ? rows.length : undefined}
      />
      <div className="kc-filters" role="tablist">
        {FILTERS.map((f) => (
          <button
            key={f.id}
            type="button"
            role="tab"
            aria-selected={status === f.id}
            className={status === f.id ? "is-on" : ""}
            onClick={() => {
              setStatus(f.id);
              load(q, f.id);
            }}
          >
            {t(f.title)}
          </button>
        ))}
      </div>

      {!data ? <div className="kw-empty">{t("Loading…")}</div> : null}
      {data && !rows.length ? (
        <div className="kw-empty">{t("No costings yet. Start one from a saree above, or make an estimate for a design.")}</div>
      ) : null}
      {rows.length ? (
        <div className="kc-table">
          <div className="kc-listrow is-head">
            <span>{t("Saree")}</span>
            <span className="num">{t("Sale price")}</span>
            <span>{t("Product")}</span>
            <span className="num">{t("Cost price")}</span>
            <span className="num">{t("Margin")}</span>
            <span>{t("Updated")}</span>
          </div>
          {rows.map((r) => (
            <Link key={r.id} to={kp.costing(r.id)} className="kc-listrow">
              <span>
                <strong>{r.sareeNumber || t("Estimate")}</strong> <small>{r.designNumber}</small>
                <span className={`kc-status is-${String(r.status).toLowerCase()}`} style={{ marginLeft: 0 }}>
                  {t(STATUS_LABEL[r.status] || r.status)} · v{r.versionNo}
                  {r.blankCount ? ` · ${t("{{n}} empty", { n: r.blankCount })}` : ""}
                </span>
              </span>
              <span className="num">
                <strong>{rupees(r.salePrice, 0)}</strong>
              </span>
              <span>{r.productName || <small>{t("Not linked")}</small>}</span>
              <span className="num">{rupees(r.costPrice, 0)}</span>
              <span className="num">{r.marginPct ? `${r.marginPct}%` : "—"}</span>
              <span>
                <small>
                  {fmtDay(localDay(r.updatedAt))} · {r.preparedByName}
                </small>
              </span>
            </Link>
          ))}
        </div>
      ) : null}

      <Modal isOpen={picking} toggle={() => setPicking(false)} centered>
        <ModalHeader toggle={() => setPicking(false)}>{t("Estimate for a design")}</ModalHeader>
        <ModalBody>
          <p className="kc-muted">{t("For a quote before weaving. It uses the design's rate card and your material rates.")}</p>
          <input
            className="form-control"
            autoFocus
            placeholder={t("Design number")}
            value={designQ}
            onChange={(e) => setDesignQ(e.target.value)}
          />
          <div className="kc-waiting" style={{ marginTop: 12 }}>
            {designs.map((d: any) => (
              <button
                key={d.id || d._id}
                type="button"
                disabled={busy}
                onClick={() => {
                  setPicking(false);
                  start({ designId: d.id || d._id });
                }}
              >
                <strong>{d.designNumber}</strong>
                {d.designName ? <small>{d.designName}</small> : null}
              </button>
            ))}
            {!designs.length ? <span className="kc-muted">{t("No designs found")}</span> : null}
          </div>
        </ModalBody>
      </Modal>
    </div>
  );
};

export default KarkhanaCostingsPage;
